import { useQuery } from '@apollo/client';
import { Popover, Transition } from '@headlessui/react';
import {
  ArrowUUpLeft,
  CalendarBlank,
  CalendarX,
  DotsThree,
  FloppyDisk,
  PlayCircle,
  StopCircle,
  TrashSimple,
} from '@phosphor-icons/react';
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Filler,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
} from 'chart.js';
import clsx from 'clsx';
import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Button } from 'src/components/Button';
import { ContentWrapper } from 'src/components/ContentWrapper';
import { DropdownMenu } from 'src/components/DropdownMenu';
import { EditableTitle } from 'src/components/EditableTitle';
import { Heading } from 'src/components/Heading';
import { Input } from 'src/components/Input';
import { Modal } from 'src/components/Modal';
import { Tab } from 'src/components/Tab';
import { Tooltip as TooltipComponent } from 'src/components/Tooltip';
import { useEnvironment } from 'src/contexts/EnvironmentContext';
import { useNotification } from 'src/contexts/NotificationContext';
import { GET_WORKFLOW_STATS } from 'src/graphql/queries';
import { withIntegrationApp } from 'src/hocs/withIntegrationApp';
import useAuthedRequest from 'src/hooks/useAuthedRequest';
import { useCampaigns } from 'src/hooks/useCampaigns';
import { QueryObject } from 'src/interfaces/queryObject.interface';
import {
  PaginatedResponse,
  WorkflowExecutionThreadStatus,
  WorkflowExecutionThreadWithComputedStatus,
  WorkflowStatus,
} from 'src/interfaces/workflows.interface';
import {
  Campaign,
  UpdateCampaignDto,
} from 'src/services/campaigns/campaigns.interface';
import { generateReferenceIdForDuplicate } from 'src/utils/referenceId';
import { dateToIsoString, isoStringToDate } from 'src/utils/timeZoneUtils';
import {
  useWizard,
  WizardProvider,
} from '../../components/wizard/WizardContext';
import { ActivityContent } from './components/ActivityContent';
import {
  CampaignAnalyticsSection,
  CampaignMetricsSkeleton,
  ChartContainerSkeleton,
} from './components/CampaignAnalyticsSection';
import { CampaignBadge, CampaignStatus } from './components/CampaignBadge';
import CommunicationContent from './components/CommunicationContent';
import ContactsContent from './components/ContactsContent';
import SettingsContent from './components/SettingsContent';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Filler,
  Title,
  Tooltip,
  Legend,
);

// Types
type WorkflowStatusDto = {
  __typename: 'WorkflowStatusesDto';
  workflowReferenceId: string;
  count: number;
  status: string;
};

type CampaignMetrics = {
  totalContacts: number;
  queued: number;
  active: number;
  errored: number;
  exited: number;
};

// Skeleton Component
const CampaignOverviewSkeleton = () => (
  <>
    <Heading
      className="border-b-0"
      title={
        <div className="flex items-start gap-2">
          <div className="flex flex-col gap-2">
            <Skeleton height={32} width={200} />
            <Skeleton height={20} width={300} />
          </div>
          <Skeleton height={24} width={80} className="ml-4" />
        </div>
      }
    >
      <div className="flex gap-2 mt-3">
        <Skeleton height={32} width={32} />
        <Skeleton height={32} width={120} />
        <Skeleton height={32} width={100} />
      </div>
    </Heading>
    <ContentWrapper className="p-0 h-auto">
      <div className="p-4 space-y-6">
        <CampaignMetricsSkeleton />
        <ChartContainerSkeleton />
      </div>
    </ContentWrapper>
  </>
);

// Main Component
const CampaignOverview = ({
  campaignReferenceId,
}: {
  campaignReferenceId: string;
}) => {
  // Hooks
  const navigate = useNavigate();
  const notification = useNotification();
  const { get, post } = useAuthedRequest();
  const campaignsService = useCampaigns();
  const { environment } = useEnvironment();
  const environmentId = environment?.envId;
  const {
    saveCampaignAutomation,
    setCampaignAutomation,
    campaignAutomation,
    isInitializing,
  } = useWizard();
  const [currentTab, setCurrentTab] = useState(0);
  const [searchParams, setSearchParams] = useSearchParams();
  const currentTabParam = searchParams.get('tab');

  // Refs
  const titleInputRef = useRef<HTMLInputElement>(null);

  // State
  const [campaign, setCampaign] = useState<Campaign | null>(null);
  const [campaignStartDate, setCampaignStartDate] = useState('');
  const [campaignName, setCampaignName] = useState('Untitled Campaign');
  const [campaignDescription, setCampaignDescription] = useState('');
  const [campaignStatus, setCampaignStatus] = useState<CampaignStatus>('draft');
  const [campaignMetrics, setCampaignMetrics] = useState<CampaignMetrics>({
    totalContacts: 0,
    queued: 0,
    active: 0,
    errored: 0,
    exited: 0,
  });

  // Loading states
  const [loading, setLoading] = useState(false);
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [savingNameAndDescription, setSavingNameAndDescription] =
    useState(false);

  // UI states
  const [isEditingCampaignDetails, setIsEditingCampaignDetails] =
    useState(false);
  const [isMoreOptionsDropdownOpen, setIsMoreOptionsDropdownOpen] =
    useState(false);
  const [showScheduleInput, setShowScheduleInput] = useState(false);
  const [deleteCampaignModalOpen, setDeleteCampaignModalOpen] = useState(false);
  const [canStartCampaign, setCanStartCampaign] = useState(false);

  // GraphQL Query
  const { data: workflowStatsData, loading: workflowStatsLoading } = useQuery(
    GET_WORKFLOW_STATS,
    {
      variables: {
        environmentId: environmentId,
        workflowReferenceIds: [campaignReferenceId!],
      },
      skip: !campaignReferenceId,
    },
  );

  // Derived state
  const canSeeScheduleInput =
    campaignStatus === 'stopped' ||
    campaignStatus === 'draft' ||
    showScheduleInput;

  // Event handlers
  const handleNameChange = (name: string) => setCampaignName(name);
  const handleDescriptionChange = (description: string) =>
    setCampaignDescription(description);

  const handleMoreOptionsClick = (item: string) => {
    setIsMoreOptionsDropdownOpen(false);

    switch (item) {
      case 'Export Calls CSV':
        navigate(`/calls?workflow_reference_id=${campaignReferenceId}`);
        break;
      case 'Duplicate Campaign':
        handleDuplicateCampaign(campaign);
        break;
    }
  };

  const handleDuplicateCampaign = async (workflow: Campaign | null) => {
    if (!workflow) return;
    try {
      setLoading(true);
      const fetchedWorkflow = await get(`/workflows/${workflow.referenceId}`);
      if (!fetchedWorkflow) {
        notification.error('Failed to duplicate campaign: campaign not found');
        return;
      }
      const duplicatedWorkflow = await post(`/workflows`, {
        ...fetchedWorkflow,
        referenceId: generateReferenceIdForDuplicate(
          fetchedWorkflow.referenceId,
        ),
        version: 1,
        id: undefined,
        createdAt: undefined,
        updatedAt: undefined,
      });
      if (!duplicatedWorkflow) {
        notification.error('Failed to duplicate campaign');
        return;
      }
      navigate('/campaigns');
      notification.success('Successfully duplicated campaign');
    } catch (error) {
      notification.error('Failed to duplicate campaign');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // API calls
  const saveNameAndDescription = async () => {
    if (!campaign) return;

    setSavingNameAndDescription(true);
    try {
      await campaignsService.updateCampaign({
        ...campaign,
        name: campaignName,
        description: campaignDescription,
      } as UpdateCampaignDto);
      notification.success('Campaign name and description saved');
    } catch {
      notification.error('Failed to save campaign details');
    } finally {
      setSavingNameAndDescription(false);
    }
  };

  const updateCampaignScheduleDate = async (campaignStartDate: string) => {
    if (!campaign) return;

    setLoading(true);
    try {
      const browserLocalTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const startDateTz = campaign.contactWindowId
        ? browserLocalTz
        : campaign.schedule?.timeZone;

      if (!startDateTz) {
        notification.error('Unable to determine campaign timezone');
        return;
      }

      await campaignsService.updateCampaign({
        ...campaign,
        campaignStartDate,
        campaignStartDateTz: startDateTz,
      });
      await campaignsService.activateCampaign(campaign.referenceId);
      await fetchCampaign();

      setShowScheduleInput(false);
      notification.success('Campaign start date updated');
    } catch {
      notification.error('Failed to update campaign schedule');
    } finally {
      setLoading(false);
    }
  };

  // Data fetching
  const fetchCampaign = async () => {
    if (!campaignReferenceId) return;

    setLoading(true);
    try {
      const campaignData =
        await campaignsService.fetchCampaign(campaignReferenceId);
      setCampaign(campaignData);
      // TODO: Move away from updating campaign fields like this and instead use the campaignAutomation state.
      setCampaignName(campaignData.name);
      setCampaignDescription(campaignData.description || '');

      if (
        !campaignData.campaignStartDate ||
        campaignData.campaignStartDate === ''
      ) {
        // If startDate is missing/empty --> Draft
        setCampaignStatus('draft');
      } else {
        switch (campaignData.status) {
          case WorkflowStatus.WORKFLOW_ACTIVE: {
            const startDateTz = campaignData.campaignStartDateTz;
            const startDateIso = startDateTz
              ? dateToIsoString(campaignData.campaignStartDate, startDateTz)
              : undefined;
            const startDate = startDateIso ? new Date(startDateIso) : undefined;
            const now = new Date();
            // Scheduled if active and startDate in future
            if (startDate && startDate > now) {
              setCampaignStatus('scheduled');
            } else {
              // Running if active and startDate not in future
              setCampaignStatus('running');
            }
            break;
          }
          case WorkflowStatus.WORKFLOW_PAUSED:
            setCampaignStatus('stopped');
            break;
        }
      }
    } catch {
      notification.error('Failed to fetch campaign');
    } finally {
      setLoading(false);
    }
  };

  const fetchContacts = async () => {
    if (!campaignReferenceId) return;

    setLoadingContacts(true);
    try {
      const queryObject: QueryObject = {
        page: '1',
        size: '1',
        filters: JSON.stringify({
          workflowReferenceId: campaignReferenceId,
        }),
        sort_column: 'thread_created_at',
        sort_desc: 'true',
      };

      const query = new URLSearchParams(queryObject as Record<string, string>);
      const contacts: PaginatedResponse<WorkflowExecutionThreadWithComputedStatus> =
        await get(`/workflow_threads?${query.toString()}`);

      setCampaignMetrics((prev) => ({
        ...prev,
        totalContacts: contacts.total,
      }));
    } catch {
      notification.error('Failed to fetch contacts');
    } finally {
      setLoadingContacts(false);
    }
  };

  const handleStartCampaign = async () => {
    if (!campaign) return;

    setLoading(true);
    try {
      const browserLocalTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const startDateTz = campaign.contactWindowId
        ? browserLocalTz
        : campaign.schedule?.timeZone;
      if (!startDateTz) {
        notification.error('Unable to determine campaign timezone');
        return;
      }
      const startDate = isoStringToDate(new Date().toISOString(), startDateTz);
      await campaignsService.updateCampaign({
        ...campaign,
        campaignStartDate: startDate,
        campaignStartDateTz: startDateTz,
      });
      await campaignsService.activateCampaign(campaign.referenceId);
      setCampaignStatus('running');
      notification.success('Campaign started');
      setShowScheduleInput(false);
    } catch {
      notification.error('Failed to start campaign');
    } finally {
      setLoading(false);
    }
  };

  const handleStopCampaign = async () => {
    if (!campaign) return;

    setLoading(true);
    try {
      await campaignsService.deactivateCampaign(campaign.referenceId);
      notification.success('Campaign stopped');
      setCampaignStatus('stopped');
      setShowScheduleInput(true);
    } catch {
      notification.error('Failed to stop campaign');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCampaign = async () => {
    if (!campaign) return;

    setLoading(true);

    try {
      await campaignsService.deleteCampaign(campaign.referenceId);
      navigate('/beta/campaigns');
      notification.success('Campaign deleted');
    } catch {
      notification.error('Failed to delete campaign');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelScheduledStart = async () => {
    if (!campaign) return;

    setLoading(true);
    try {
      await campaignsService.updateCampaign({
        ...campaign,
        campaignStartDate: undefined,
        campaignStartDateTz: undefined,
      });
      await campaignsService.deactivateCampaign(campaign.referenceId);
      await fetchCampaign();
      notification.success('Campaign scheduled start cancelled');
      setCampaignStatus('stopped');
      setShowScheduleInput(false);
    } catch {
      notification.error('Failed to cancel scheduled start');
    } finally {
      setLoading(false);
    }
  };
  // Sync name and description to campaignAutomation when they change
  useEffect(() => {
    setCampaignAutomation({
      ...campaignAutomation,
      name: campaignName,
      description: campaignDescription,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignName, campaignDescription]);

  // Effects
  useEffect(() => {
    fetchCampaign();
    fetchContacts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignReferenceId]);

  const handleSave = async (exit: boolean = false) => {
    setLoading(true);
    try {
      const campaignSaved = await saveCampaignAutomation();
      if (campaignSaved) {
        notification.success('Campaign saved successfully');
        setIsEditingCampaignDetails(false);
        if (exit) {
          navigate('/beta/campaigns');
        }
      }
      fetchCampaign();
      fetchContacts();
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (workflowStatsData) {
      const workflowStats = workflowStatsData.getWorkflowStats;

      const queued =
        (workflowStats.find(
          (stat: WorkflowStatusDto) =>
            stat.status === WorkflowExecutionThreadStatus.RUNNING,
        )?.count || 0) +
        (workflowStats.find(
          (stat: WorkflowStatusDto) =>
            stat.status === WorkflowExecutionThreadStatus.RATE_LIMITED,
        )?.count || 0);

      const active =
        workflowStats.find(
          (stat: WorkflowStatusDto) =>
            stat.status === WorkflowExecutionThreadStatus.PAUSED,
        )?.count || 0;

      const errored =
        workflowStats.find(
          (stat: WorkflowStatusDto) =>
            stat.status === WorkflowExecutionThreadStatus.ERRORED,
        )?.count || 0;

      const exited =
        workflowStats.find(
          (stat: WorkflowStatusDto) =>
            stat.status === WorkflowExecutionThreadStatus.COMPLETED,
        )?.count || 0;

      setCampaignMetrics((prev) => ({
        ...prev,
        queued,
        active,
        errored,
        exited,
      }));
    }
  }, [workflowStatsData]);

  // Computed values
  const moreOptionsItems = ['Export Calls CSV', 'Duplicate Campaign'];

  const tabItems = [
    {
      label: 'Overview',
      content: (
        <div className="p-4 space-y-6">
          <CampaignAnalyticsSection
            metrics={campaignMetrics}
            startDate={
              campaign?.campaignStartDate && campaign.campaignStartDateTz
                ? new Date(
                    dateToIsoString(
                      campaign.campaignStartDate,
                      campaign.campaignStartDateTz,
                    ),
                  )
                : undefined
            }
            loadingContacts={loadingContacts}
            workflowStatsLoading={workflowStatsLoading}
          />
        </div>
      ),
    },
    {
      label: 'Communication',
      content: <CommunicationContent disabled={campaignStatus !== 'draft'} />,
    },
    {
      label: 'Contacts',
      content: <ContactsContent onEditCrmIntegration={() => goToSettings()} />,
    },
    {
      label: 'Activity',
      content: <ActivityContent />,
    },
    {
      label: 'Settings',
      content: <SettingsContent />,
    },
  ];

  const SETTINGS_TAB_INDEX = tabItems.findIndex(
    (item) => item.label === 'Settings',
  );

  const goToSettings = () => {
    setCurrentTab(SETTINGS_TAB_INDEX);
  };

  useEffect(() => {
    if (currentTabParam) {
      const tabLabels = [
        'Overview',
        'Communication',
        'Contacts',
        'Activity',
        'Settings',
      ];
      const tabIndex = tabLabels.findIndex(
        (label) => label.toLowerCase() === currentTabParam.toLowerCase(),
      );
      if (tabIndex !== -1) {
        setCurrentTab(tabIndex);
      }
      setSearchParams(undefined);
    }
  }, [currentTabParam, setSearchParams]);

  useEffect(() => {
    if (!campaign) return;

    setCanStartCampaign(
      campaignMetrics.totalContacts > 0 ||
        !!campaign.crmCampaignConfig?.provider,
    );
  }, [campaign, campaignMetrics]);

  const getStartCampaignTooltipMessage = useCallback((action: string) => {
    return `You need to have at least one contact in your campaign or a CRM configured to ${action} a campaign.`;
  }, []);

  if (loading) return <CampaignOverviewSkeleton />;

  return (
    <>
      <Heading
        className="border-b-0"
        fixedHeader={true}
        title={
          <div>
            <div className="flex items-start gap-2">
              <EditableTitle
                onSave={saveNameAndDescription}
                titleInputRef={titleInputRef}
                metadata=""
                titlePlaceholder="Campaign Name"
                subtitlePlaceholder="Add a description"
                onNameChange={handleNameChange}
                onDescriptionChange={handleDescriptionChange}
                loading={loading || savingNameAndDescription}
                isEditing={isEditingCampaignDetails}
                setIsEditing={setIsEditingCampaignDetails}
                name={campaignName}
                description={campaignDescription}
                containerClassName={clsx(
                  savingNameAndDescription && 'opacity-50',
                )}
              />
              <CampaignBadge status={campaignStatus} className="ml-4" />
            </div>
            <div className="flex items-start gap-2">
              <div className="text-neutral-600 italic">
                ID: {campaignReferenceId}
              </div>
            </div>
          </div>
        }
      >
        <div className="flex flex-col gap-2 mt-3 items-center md:flex-row md:gap-2">
          {/* TODO: Add back in when we have more options */}
          <div className="relative">
            <Button
              variant="text"
              size="small"
              onClick={() =>
                setIsMoreOptionsDropdownOpen(!isMoreOptionsDropdownOpen)
              }
            >
              <DotsThree size={24} />
            </Button>
            <DropdownMenu
              isOpen={isMoreOptionsDropdownOpen}
              position="bottom-right"
              onItemClick={handleMoreOptionsClick}
              className="min-w-[200px]"
              items={moreOptionsItems}
            />
          </div>

          {campaignStatus === 'stopped' && (
            <Button
              variant="outlined"
              size="small"
              className="text-error border-error/20 hover:bg-error/5"
              onClick={() => setDeleteCampaignModalOpen(true)}
            >
              <TrashSimple size={16} className="mr-2" />
              Delete Campaign
            </Button>
          )}

          {canSeeScheduleInput && (
            <Popover className="relative">
              {({ open }) => (
                <TooltipComponent
                  position="bottom"
                  content={
                    !canStartCampaign
                      ? getStartCampaignTooltipMessage('schedule')
                      : ''
                  }
                >
                  <Popover.Button as={Fragment}>
                    <Button
                      variant="outlined"
                      size="small"
                      className={open ? 'ring-2 ring-primary/20' : ''}
                      disabled={!canStartCampaign}
                    >
                      <CalendarBlank size={16} className="mr-2" />
                      Schedule Start
                    </Button>
                  </Popover.Button>
                  <Transition
                    as={Fragment}
                    enter="transition ease-out duration-200"
                    enterFrom="opacity-0 translate-y-1"
                    enterTo="opacity-100 translate-y-0"
                    leave="transition ease-in duration-150"
                    leaveFrom="opacity-100 translate-y-0"
                    leaveTo="opacity-0 translate-y-1"
                  >
                    <Popover.Panel className="absolute right-0 z-10 mt-1 w-56">
                      <div className="bg-neutral-0 shadow-lg rounded-md py-1 overflow-auto focus:outline-none text-sm border border-neutral-100">
                        <div className="px-3 py-2.5 gap-2 flex flex-col">
                          <Input
                            id="campaign-start-date"
                            label="Campaign Start Date"
                            type="date"
                            value={campaignStartDate}
                            onChange={(
                              e: React.ChangeEvent<HTMLInputElement>,
                            ) => setCampaignStartDate(e.target.value)}
                            className="max-w-xs"
                            required={true}
                          />

                          <Button
                            variant="outlined"
                            size="small"
                            disabled={
                              isInitializing ||
                              !campaignStartDate ||
                              campaignStartDate.trim() === ''
                            }
                            onClick={() => {
                              updateCampaignScheduleDate(campaignStartDate);
                            }}
                          >
                            Set Schedule
                          </Button>
                        </div>
                      </div>
                    </Popover.Panel>
                  </Transition>
                </TooltipComponent>
              )}
            </Popover>
          )}

          {campaignStatus === 'running' && (
            <Button
              variant="outlined"
              size="small"
              className="text-error border-error/20 hover:bg-error/5"
              onClick={handleStopCampaign}
            >
              <StopCircle size={16} className="mr-2" />
              Stop
            </Button>
          )}

          {(campaignStatus === 'stopped' || campaignStatus === 'draft') && (
            <TooltipComponent
              position="bottom"
              content={
                !canStartCampaign ? getStartCampaignTooltipMessage('start') : ''
              }
            >
              <Button
                size="small"
                onClick={handleStartCampaign}
                disabled={!canStartCampaign}
              >
                <PlayCircle size={16} className="mr-2" />
                Start
              </Button>
            </TooltipComponent>
          )}

          {campaignStatus === 'scheduled' && (
            <Button
              size="small"
              variant="outlined"
              color="error"
              onClick={() => handleCancelScheduledStart()}
            >
              <CalendarX size={16} className="mr-2" />
              Cancel Scheduled Start
            </Button>
          )}

          <div
            className="mx-1 h-4 w-px bg-neutral-200 select-none pointer-events-none hidden md:block"
            aria-hidden="true"
          ></div>

          <Button
            size="small"
            variant="outlined"
            onClick={() => handleSave(true)}
          >
            <ArrowUUpLeft size={16} className="mr-2" />
            Save and exit
          </Button>

          <Button size="small" onClick={() => handleSave(false)}>
            <FloppyDisk size={16} className="mr-2" />
            Save
          </Button>
        </div>
      </Heading>

      <ContentWrapper className="p-0 h-auto">
        <Tab
          items={tabItems}
          selectedIndex={currentTab}
          onChange={setCurrentTab}
        />
      </ContentWrapper>

      <Modal
        title={`Delete Campaign: ${campaignName || ''}`}
        isOpen={deleteCampaignModalOpen}
        onClose={() => {
          setDeleteCampaignModalOpen(false);
        }}
        actionButton={
          <Button onClick={handleDeleteCampaign}>Delete Campaign</Button>
        }
        className="w-auto max-w-md"
      >
        <div className="text-center">
          Are you sure you want to delete this campaign?
        </div>
      </Modal>
    </>
  );
};

const CampaignOverviewWithWizard = () => {
  const { id: campaignReferenceId } = useParams();

  return (
    <WizardProvider isEditing editReferenceId={campaignReferenceId}>
      <CampaignOverview campaignReferenceId={campaignReferenceId!} />
    </WizardProvider>
  );
};

export default withIntegrationApp(CampaignOverviewWithWizard);
